/**
 * AuthGate — Login-Gate für den optionalen Basic-Auth-Schutz (v0.3.8).
 *
 * Prüft beim Start GET /auth/check. Ist der Schutz aktiv und fehlen gültige
 * Zugangsdaten, wird statt der App ein Login-Formular gezeigt; die
 * Zugangsdaten gehen danach als Basic-Header an jede API-Anfrage
 * (attachAuth / installFetchAuth). Antwortet die API später mit 401
 * (z.B. serverseitig geänderte Passwörter), meldet sich das Gate zurück.
 */

import { useCallback, useEffect, useState } from 'react'
import { Loader2, Lock, LogIn } from 'lucide-react'
import {
  AUTH_REQUIRED_EVENT,
  authHeader,
  clearAuth,
  setAuthCredentials,
} from '../lib/apiAuth'

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:8899'

type GateState = 'checking' | 'ready' | 'login' | 'offline'

export function AuthGate({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<GateState>('checking')
  const [error, setError] = useState<string | null>(null)
  const [user, setUser] = useState('')
  const [password, setPassword] = useState('')
  const [submitting, setSubmitting] = useState(false)

  const check = useCallback(async (afterLogin = false) => {
    try {
      const res = await fetch(`${API_BASE}/auth/check`, { headers: authHeader() })
      if (res.ok) {
        setError(null)
        setState('ready')
        if (afterLogin) {
          // Laufende react-query-Caches können 401-Fehler halten — sauber neu starten
          window.location.reload()
        }
        return
      }
      if (res.status === 401) {
        setError(afterLogin ? 'Zugangsdaten ungültig.' : null)
        setState('login')
        return
      }
      if (res.status === 429) {
        setError('Zu viele Fehlversuche – bitte kurz warten und erneut versuchen.')
        setState('login')
        return
      }
      setError(`Unerwartete Antwort (${res.status}).`)
      setState('login')
    } catch {
      setError(null)
      setState('offline')
    }
  }, [])

  useEffect(() => {
    check()
  }, [check])

  // Spätere 401-Antworten (Passwort serverseitig geändert etc.)
  useEffect(() => {
    const onAuthRequired = () => {
      clearAuth()
      setState((s) => (s === 'ready' ? 'login' : s))
    }
    window.addEventListener(AUTH_REQUIRED_EVENT, onAuthRequired)
    return () => window.removeEventListener(AUTH_REQUIRED_EVENT, onAuthRequired)
  }, [])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!user || !password || submitting) return
    setSubmitting(true)
    setAuthCredentials(user, password)
    await check(true)
    setSubmitting(false)
  }

  if (state === 'ready') {
    return <>{children}</>
  }

  if (state === 'checking') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    )
  }

  if (state === 'offline') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="text-center">
          <p className="text-gray-700 font-medium">Backend nicht erreichbar</p>
          <p className="text-sm text-gray-500 mt-1">{API_BASE}</p>
          <button
            onClick={() => {
              setState('checking')
              check()
            }}
            className="mt-4 px-4 py-2 text-sm bg-gray-900 text-white rounded-lg hover:bg-gray-700"
          >
            Erneut versuchen
          </button>
        </div>
      </div>
    )
  }

  // Login
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
      <div className="w-full max-w-sm bg-white rounded-xl shadow-md border border-gray-200 p-8">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-full bg-gray-900 text-white flex items-center justify-center">
            <Lock className="w-5 h-5" />
          </div>
          <div>
            <h1 className="text-lg font-semibold text-gray-900">Lagewerkzeug</h1>
            <p className="text-xs text-gray-500">Anmeldung erforderlich</p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Benutzername
            </label>
            <input
              type="text"
              value={user}
              onChange={(e) => setUser(e.target.value)}
              autoComplete="username"
              autoFocus
              className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-gray-900 focus:ring-gray-900 text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Passwort
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-gray-900 focus:ring-gray-900 text-sm"
            />
          </div>

          {error && (
            <p className="text-sm text-red-600" role="alert">
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={!user || !password || submitting}
            className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-gray-900 text-white hover:bg-gray-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
          >
            {submitting ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <LogIn className="w-4 h-4" />
            )}
            Anmelden
          </button>
        </form>
      </div>
    </div>
  )
}
